import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { ShoppingBag, ExternalLink, Star, Info, ArrowRight, ChevronDown, Check, X } from "lucide-react";

export default function Equipment() {
  const [categories, setCategories] = useState(null);
  const [tier, setTier] = useState("all");

  useSEO({
    title: "Equipment Recommendations — Boxing Gear Coach Chris Trusts | Better Through Boxing",
    description: "Coach Chris Persaud's curated boxing equipment recommendations — gloves, hand wraps, shoes, head gear, mouthpieces, heavy bags, double-end bags, and cobra bags. Vetted gear for every skill level, by The Flow State Method LLC.",
    path: "/equipment",
    keywords: [
      "best boxing gloves", "best hand wraps", "best boxing shoes", "boxing equipment recommendations",
      "double end bag", "cobra bag", "heavy bag for home", "boxing gear for beginners",
      "Coach Chris boxing gear", "Flow State Method recommendations", "Better Through Boxing equipment",
    ],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/equipment");
        if (!cancelled) setCategories(data || []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Price-tier filter (matches the mobile app). Filters products within each category,
  // recomputes the top-picks/others split, and drops categories with no match.
  const TIERS = [
    { id: "all", label: "All" },
    { id: "$", label: "$" },
    { id: "$$", label: "$$" },
    { id: "$$$", label: "$$$" },
  ];
  const hasAnyProducts = (categories || []).some((c) => (c.products || []).length > 0);
  const visibleCategories = (categories || [])
    .map((c) => {
      const products = (c.products || []).filter((p) => tier === "all" || p.price_tier === tier);
      if (!products.length) return null;
      return {
        ...c,
        products,
        top_picks: products.filter((p) => p.is_top_pick),
        others: products.filter((p) => !p.is_top_pick),
      };
    })
    .filter(Boolean);

  // Inject ItemList JSON-LD schema for SEO once products load
  useEffect(() => {
    if (!categories || categories.length === 0) return;
    const id = "equipment-jsonld";
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const items = [];
    let pos = 1;
    categories.forEach((c) => (c.products || []).forEach((p) => {
      items.push({
        "@type": "ListItem",
        position: pos++,
        item: {
          "@type": "Product",
          name: p.title,
          brand: p.brand || undefined,
          image: p.image || undefined,
          description: p.coach_notes || undefined,
          category: c.name,
          offers: {
            "@type": "Offer",
            url: p.affiliate_url,
            availability: "https://schema.org/InStock",
            priceCurrency: "USD",
          },
        },
      });
    }));
    if (!items.length) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Coach Chris's Boxing Equipment Recommendations",
      itemListElement: items,
    };
    const tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    tag.text = JSON.stringify(schema);
    document.head.appendChild(tag);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, [categories]);

  return (
    <div className="App relative">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="equipment-page">
        {/* SEO-rich hero — always rendered */}
        <header className="mb-10 max-w-3xl">
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3 flex items-center gap-2">
            <ShoppingBag className="h-3 w-3" /> Coach&apos;s gear locker
          </div>
          <h1 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-none mb-6">Gear I<br />recommend.</h1>
          <p className="text-zinc-300 text-base md:text-lg leading-relaxed mb-4">
            Curated boxing equipment recommendations from Coach Chris Persaud — gloves, wraps, bags, mouthpieces, and more, vetted across price points and skill levels. Some pieces I&apos;ve trained with for years, others I trust because of the brand, the build, and what my clients report back. Either way, nothing on this page is here by accident.
          </p>
          <p className="text-zinc-400 leading-relaxed">
            New to boxing? Start with the <a href="#hand-wraps" className="text-[#FF331F] hover:underline">Hand Wraps</a> and a beginner pair of <a href="#boxing-gloves" className="text-[#FF331F] hover:underline">Gloves</a>. Building a home gym? Jump to the <a href="#heavy-bags" className="text-[#FF331F] hover:underline">Heavy Bags</a> and <a href="#double-end-bags" className="text-[#FF331F] hover:underline">Double-End Bags</a> sections.
          </p>
        </header>

        {/* FTC disclosure */}
        <div className="border border-[#27272A] bg-[#0F0F0F] p-4 md:p-5 mb-12 flex items-start gap-3" data-testid="ftc-disclosure">
          <Info className="h-4 w-4 text-[#FF331F] mt-0.5 shrink-0" />
          <p className="text-xs text-zinc-400 leading-relaxed">
            <strong className="text-zinc-200">Affiliate disclosure:</strong> Some links on this page are affiliate links — if you buy through them I may earn a small commission at no extra cost to you. I only recommend gear I trust — either from years of personal use or based on the brand&apos;s reputation and what my clients report back. Your support helps keep the lights on at The Flow State Method LLC.
          </p>
        </div>

        {/* Loading state — keep static content visible above */}
        {categories === null && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="equipment-loading">
            {[0, 1].map((i) => (
              <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-6 animate-pulse">
                <div className="aspect-[16/9] bg-[#1A1A1A] mb-4" />
                <div className="h-5 w-3/4 bg-[#1A1A1A] mb-2" />
                <div className="h-3 w-1/2 bg-[#1A1A1A]" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {categories && categories.length === 0 && (
          <div className="border border-[#27272A] bg-[#0F0F0F] p-10" data-testid="equipment-empty">
            <h2 className="font-display uppercase text-2xl text-white mb-3">Coming soon.</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">Coach Chris is building out his gear recommendations right now. Check back in a few days — the <a href="https://www.instagram.com/flow_state_method/" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline">Instagram</a> drops first when it&apos;s live.</p>
            <Link to="/services" className="inline-flex items-center text-[#FF331F] text-xs uppercase tracking-widest font-bold">See coaching options <ArrowRight className="h-3 w-3 ml-1" /></Link>
          </div>
        )}

        {/* Magazine layout — sidebar + sections */}
        {categories && hasAnyProducts && (
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Sticky sidebar */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-44 space-y-1" data-testid="equipment-sidebar">
                {/* Price-tier filter */}
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-3">Price range</div>
                <div className="flex gap-2 mb-7" data-testid="equipment-tier-filter">
                  {TIERS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTier(t.id)}
                      data-testid={`equipment-tier-${t.id}`}
                      className={`flex-1 py-2 text-sm font-bold tracking-wide border transition-colors ${
                        tier === t.id
                          ? "border-[#FF331F] text-[#FF331F] bg-[#FF331F]/10"
                          : "border-[#27272A] text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-3">Jump to</div>
                {visibleCategories.map((c) => (
                  <a key={c.slug} href={`#${c.slug}`} className="block py-1.5 text-sm text-zinc-300 hover:text-[#FF331F] border-l-2 border-transparent hover:border-[#FF331F] pl-3 transition-colors">
                    {c.name} <span className="text-zinc-600 text-xs">({(c.products || []).length})</span>
                  </a>
                ))}
              </div>
            </aside>

            {/* Sections */}
            <div className="lg:col-span-9 space-y-20">
              {visibleCategories.length === 0 ? (
                <div className="border border-[#27272A] bg-[#0F0F0F] p-10" data-testid="equipment-tier-empty">
                  <h2 className="font-display uppercase text-2xl text-white mb-3">No gear in this range.</h2>
                  <p className="text-zinc-300 leading-relaxed mb-4">Nothing tagged &ldquo;{tier}&rdquo; yet. Try another price range.</p>
                  <button type="button" onClick={() => setTier("all")} className="inline-flex items-center text-[#FF331F] text-xs uppercase tracking-widest font-bold" data-testid="equipment-tier-reset">
                    Show all gear <ArrowRight className="h-3 w-3 ml-1" />
                  </button>
                </div>
              ) : (
                visibleCategories.map((c) => (
                <section key={c.slug} id={c.slug} className="scroll-mt-40" data-testid={`equipment-section-${c.slug}`}>
                  <div className="border-b border-[#27272A] pb-4 mb-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Category</div>
                    <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight mb-3">{c.name}</h2>
                    {c.description && <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl">{c.description}</p>}
                  </div>

                  {/* Coach's Top picks — highlighted, same image size as the rest */}
                  {c.top_picks && c.top_picks.length > 0 && (
                    <div className="mb-10">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-4 flex items-center gap-2">
                        <Star className="h-3 w-3 fill-[#FF331F]" /> Coach&apos;s Top {c.top_picks.length}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                        {c.top_picks.map((p) => <ProductCard key={p.id} p={p} featured />)}
                      </div>
                    </div>
                  )}

                  {/* Other recommendations */}
                  {c.others && c.others.length > 0 && (
                    <div>
                      {c.top_picks && c.top_picks.length > 0 && (
                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-4">Also worth a look</div>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                        {c.others.map((p) => <ProductCard key={p.id} p={p} />)}
                      </div>
                    </div>
                  )}
                </section>
                ))
              )}
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function SkillBadge({ level }) {
  if (!level || level === "All") return null;
  const color = level === "Beginner" ? "text-emerald-300 bg-emerald-950/40 border-emerald-800" :
    level === "Intermediate" ? "text-amber-300 bg-amber-950/40 border-amber-800" :
    "text-red-300 bg-red-950/40 border-red-800";
  return <span className={`text-[9px] uppercase tracking-widest font-bold border px-1.5 py-0.5 ${color}`}>{level}</span>;
}

function ProductCard({ p, featured }) {
  const [expanded, setExpanded] = useState(false);
  const pros = Array.isArray(p.pros) ? p.pros.filter(Boolean) : [];
  const cons = Array.isArray(p.cons) ? p.cons.filter(Boolean) : [];
  const hasDetails = !!p.coach_notes || pros.length > 0 || cons.length > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={() => setExpanded((v) => !v)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded((v) => !v); } }}
      className={`group border bg-[#0F0F0F] transition-colors flex flex-col cursor-pointer ${
        featured ? "border-[#FF331F]/50 hover:border-[#FF331F]" : "border-[#27272A] hover:border-[#FF331F]/50"
      }`}
      data-testid={`equipment-${featured ? "top" : "other"}-${p.slug}`}
    >
      <div className="relative aspect-square bg-[#050505] overflow-hidden">
        {p.image ? (
          <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700"><ShoppingBag className="h-8 w-8" /></div>
        )}
        {featured && (
          <div className="absolute top-2 left-2 bg-[#FF331F] text-white text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 flex items-center gap-1">
            <Star className="h-2.5 w-2.5 fill-white" /> Top pick
          </div>
        )}
        {p.price_tier && (
          <div className="absolute top-2 right-2 bg-black/70 text-white text-[11px] font-bold px-1.5 py-0.5">{p.price_tier}</div>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1 truncate">{p.brand || p.retailer || "Recommended"}</div>
        <h3 className={`font-bold text-white text-sm leading-tight mb-1.5 group-hover:text-[#FF331F] transition-colors ${expanded ? "" : "line-clamp-2"}`}>{p.title}</h3>
        {p.coach_notes && <p className={`text-xs text-zinc-400 leading-relaxed mb-2 ${expanded ? "" : "line-clamp-2"}`}>{p.coach_notes}</p>}

        {expanded && (pros.length > 0 || cons.length > 0) && (
          <div className="space-y-2 mb-2" data-testid={`equipment-details-${p.slug}`}>
            {pros.length > 0 && (
              <ul className="space-y-1">
                {pros.map((pro, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-zinc-300 leading-snug">
                    <Check className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" /> <span>{pro}</span>
                  </li>
                ))}
              </ul>
            )}
            {cons.length > 0 && (
              <ul className="space-y-1">
                {cons.map((con, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-zinc-400 leading-snug">
                    <X className="h-3 w-3 text-[#FF331F] mt-0.5 shrink-0" /> <span>{con}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-auto pt-2 border-t border-[#27272A] flex items-center justify-between gap-2">
          <SkillBadge level={p.skill_level} />
          <div className="flex items-center gap-3 ml-auto">
            {hasDetails && (
              <span className="text-zinc-500 inline-flex items-center" aria-hidden>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </span>
            )}
            <a
              href={p.affiliate_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={(e) => e.stopPropagation()}
              data-testid={`equipment-seeit-${p.slug}`}
              className="text-[10px] text-[#FF331F] uppercase tracking-widest font-bold inline-flex items-center whitespace-nowrap hover:underline"
            >
              See it <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
